import React from "react";
import {Page, Text, View, Document, StyleSheet, Font, Image} from "@react-pdf/renderer";
import logoTpsWithAddress from "../../../../../statics/logos/logoTpsWithAddress.jpg";
import OpenSansRegular from "../../../../../statics/typos/openSansRegular.ttf";
import OpenSansSemiBold from "../../../../../statics/typos/openSansSemiBold.ttf";
import OpenSansBold from "../../../../../statics/typos/openSansBold.ttf";
import {DateUtils} from "../../../../../helpers/dateUtils";
import {ManifestDataForFileModel} from "../../../../models/manifest";
import {LoadingTypeFirstLetter} from "../../../../models/enums";
import {getContainerSize} from "../../../../../helpers/containerSize";
import {Utils} from "../../../../../helpers/utils";

Font.register({
    family: "OpenSans",
    format: "truetype",
    src: OpenSansRegular
});
Font.register({
    family: "OpenSansSemiBold",
    format: "truetype",
    src: OpenSansSemiBold
});
Font.register({
    family: "OpenSansBold",
    format: "truetype",
    src: OpenSansBold
});

const styles = StyleSheet.create({
    page: {
        paddingTop: 30,
        paddingBottom: 30,
        paddingLeft: 15,
        paddingRight: 15,
        backgroundColor: "#FFF",
        fontFamily: "OpenSans"
    },
    viewer: {
        width: window.innerWidth,
        height: window.innerHeight,
    },
    header: {
        width: "100%",
        flexDirection: "column",
        alignItems: "center"
    },
    headerImage: {
        height: 45
    },
    title: {
        marginTop: 10,
        marginBottom: 10,
        fontSize: 14,
        fontFamily: "OpenSansBold"
    },
    generalData: {
        flexDirection: "column",
        fontSize: 9,
        fontFamily: "OpenSans",
        marginBottom: 10
    },
    generalDataRow: {
        flexDirection: "row",
    },
    generalDataPart: {
        flexDirection: "row",
        flexWrap: "wrap",
        width: 180,
        maxWidth: 180,
        marginRight: 10,
        marginBottom: 7
    },
    generalDataValue: {
        flexWrap: "wrap",
        marginLeft: 5,
        fontFamily: "OpenSansSemiBold"
    },
    table: {
        width: "100%",
        fontSize: 8,
        borderStyle: "solid",
        borderColor: "#000",
        borderTopWidth: 1
    },
    tableRow: {
        flexDirection: "row",
        borderLeftWidth: 1
    },
    tableCol: {
        borderStyle: "solid",
        borderColor: "#000",
        borderRightWidth: 1,
        borderBottomWidth: 1,
        paddingLeft: 1
    }
});

interface ManifestFileFileProps {
    manifest: ManifestDataForFileModel | null | undefined
}

export default function ManifestFile(props: ManifestFileFileProps): React.ReactElement {
    if (!props.manifest || !props.manifest?.loadingEntries) {
        return <Document><Page/></Document>;
    }

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <View>
                    {renderHeader()}
                    {renderGeneralData()}
                    {renderTable()}
                </View>
            </Page>
        </Document>
    );

    function renderHeader() {
        return (
            <View style={styles.header}>
                <Image style={styles.headerImage} src={logoTpsWithAddress}/>
                <Text style={styles.title}>
                    MANIFEST
                </Text>
            </View>
        );
    }

    function renderGeneralData() {
        return (
            <View style={styles.generalData}>
                <View style={styles.generalDataRow}>
                    <View style={styles.generalDataPart}>
                        <Text>Prom</Text>
                        <Text style={styles.generalDataValue}>{props.manifest!.ferryName}</Text>
                    </View>
                    <View style={styles.generalDataPart}>
                        <Text>Nr IMO Statku</Text>
                        <Text style={styles.generalDataValue}>{props.manifest!.ferryImoCode}</Text>
                    </View>
                    <View style={styles.generalDataPart}>
                        <Text>Nazwa Armatora</Text>
                        <Text style={styles.generalDataValue}>{props.manifest!.shipownerName}</Text>
                    </View>
                    <View style={styles.generalDataPart}>
                        <Text>IMO Armatora</Text>
                        <Text style={styles.generalDataValue}>{props.manifest!.shipownerImoCode}</Text>
                    </View>
                </View>
                <View style={styles.generalDataRow}>
                    <View style={styles.generalDataPart}>
                        <Text>Nr Podróży</Text>
                        <Text style={styles.generalDataValue}>{props.manifest!.voyageNo}</Text>
                    </View>
                    <View style={styles.generalDataPart}>
                        <Text>Port Przybycia/Wyjścia</Text>
                        <Text style={styles.generalDataValue}>{props.manifest!.arrivalDeparturePort}</Text>
                    </View>

                    <View style={styles.generalDataPart}>
                        <Text>Czas Wejścia/Wyjścia</Text>
                        <Text
                            style={styles.generalDataValue}>{DateUtils.getFormattedDateWithTime(props.manifest!.arrivalDepartureTime)}</Text>
                    </View>
                </View>
            </View>
        );
    }

    function renderTable() {
        return (
            <View style={styles.table}>
                <View style={styles.tableRow}>
                    <View style={[styles.tableCol, {width: 14}]}>
                        <Text>
                            LP
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 45}]}>
                        <Text>
                            Numer{"\n"}rezerwacji{"\n"}biletu
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 33}]}>
                        <Text>
                            Typ{"\n"}ładunku
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            Nr{"\n"}ciągnika
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            Nr{"\n"}naczepy
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 35}]}>
                        <Text>
                            Długość{"\n"}ładunku
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            Nazwa{"\n"}ładunku
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 36}]}>
                        <Text>
                            Waga{"\n"}ładunku
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 34}]}>
                        <Text>
                            Podłącz{"\n"}enie
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 30}]}>
                        <Text>
                            Klasa{"\n"}IMDG
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 30}]}>
                        <Text>
                            Płatny{"\n"}przez
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 40}]}>
                        <Text>
                            Spedytor{"\n"}drogowy
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 41}]}>
                        <Text>
                            Kraj{"\n"}ładunku
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 46}]}>
                        <Text>
                            Kraj{"\n"}rozładunku
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            Ponadga{"\n"}barytowy
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            Fracht
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            Waluta
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            Nr{"\n"}kontenera
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 43}]}>
                        <Text>
                            Rozm.{"\n"}kontenera
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            Waga{"\n"}VGM
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 33}]}>
                        <Text>
                            Konosa{"\n"}ment
                        </Text>
                    </View>
                </View>
                {renderTableData()}
            </View>
        );
    }

    function renderTableData() {
        const tableData: JSX.Element[] = [];

        props.manifest!.loadingEntries.forEach((row, index) => {
            tableData.push(
                <View style={styles.tableRow} wrap={false} key={index}>
                    <View style={[styles.tableCol, {width: 14}]}>
                        <Text>
                            {index + 1}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 45}]}>
                        <Text>
                            {Utils.wrapText(row.bookingNo, 9, "")}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 33}]}>
                        <Text>
                            {LoadingTypeFirstLetter[row.loadingType]}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            {row.lorryRegNo}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            {row.trailerRegNo}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 35}]}>
                        <Text>
                            {row.length}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            {row.cargoDesc}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 36}]}>
                        <Text>
                            {row.weight}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 34}]}>
                        <Text>
                            {Utils.renderBooleanValueInFile(row.plugin)}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 30}]}>
                        <Text>
                            {row.imdgClass}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 30}]}>
                        <Text>
                            {row.payableBy}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 40}]}>
                        <Text>
                            {row.shipperName}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 41}]}>
                        <Text>
                            {row.originCountry}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 46}]}>
                        <Text>
                            {row.destinationCountry}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            {Utils.renderBooleanValueInFile(row.overwide)}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            {row.freight}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            {row.currency}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 49}]}>
                        <Text>
                            {Utils.wrapText(row.containerNo!, 9, "")}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 43}]}>
                        <Text>
                            {getContainerSize(row.containerSize)}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 39}]}>
                        <Text>
                            {row.vgm}
                        </Text>
                    </View>
                    <View style={[styles.tableCol, {width: 33}]}>
                        <Text>
                            {Utils.renderBooleanValueInFile(row.billOfLading)}
                        </Text>
                    </View>
                </View>);
        });

        return tableData;
    }
}

